/**
 * API service for the in-app notification system.
 * Uses the same base URL and auth pattern as the main API service.
 */

import { fetchAuthSession } from 'aws-amplify/auth';
import type { NotificationRecord, NotificationPreferences } from '@/types/notifications';

/** API base URL from environment */
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

/**
 * Authentication error (401).
 */
export class AuthError extends Error {
  constructor(message: string = 'Session expired') {
    super(message);
    this.name = 'AuthError';
  }
}

/**
 * Get the current auth token.
 */
async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) {
    throw new AuthError('No valid session');
  }
  return token;
}

/**
 * Perform an authenticated fetch, throwing AuthError on 401.
 */
async function authFetch(endpoint: string, options: RequestInit = {}): Promise<Response> {
  const token = await getToken();

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (response.status === 401) {
    throw new AuthError('Session expired');
  }

  return response;
}

/**
 * Fetch notifications list, optionally filtered to unread only.
 */
export async function fetchNotifications(
  status?: 'unread'
): Promise<NotificationRecord[]> {
  const query = status ? `?status=${status}` : '';
  const response = await authFetch(`/notifications${query}`);

  if (!response.ok) {
    throw new Error(`Failed to fetch notifications: ${response.status}`);
  }

  const data = await response.json();
  return data.notifications;
}

/**
 * Fetch the unread notification count.
 */
export async function fetchUnreadCount(): Promise<number> {
  const response = await authFetch('/notifications/count');

  if (!response.ok) {
    throw new Error(`Failed to fetch unread count: ${response.status}`);
  }

  const data = await response.json();
  return data.count;
}

/**
 * Mark a single notification as read.
 */
export async function markNotificationRead(
  notificationId: string
): Promise<NotificationRecord> {
  const response = await authFetch(`/notifications/${notificationId}/read`, {
    method: 'PATCH',
  });

  if (!response.ok) {
    throw new Error(`Failed to mark notification read: ${response.status}`);
  }

  const data = await response.json();
  return data.notification;
}

/**
 * Mark all notifications as read.
 */
export async function markAllRead(): Promise<number> {
  const response = await authFetch('/notifications/read-all', {
    method: 'POST',
  });

  if (!response.ok) {
    throw new Error(`Failed to mark all read: ${response.status}`);
  }

  const data = await response.json();
  return data.updated_count;
}

/**
 * Delete a notification.
 */
export async function deleteNotification(notificationId: string): Promise<void> {
  const response = await authFetch(`/notifications/${notificationId}`, {
    method: 'DELETE',
  });

  if (!response.ok && response.status !== 204) {
    throw new Error(`Failed to delete notification: ${response.status}`);
  }
}

/**
 * Fetch notification channel preferences.
 */
export async function fetchPreferences(): Promise<NotificationPreferences> {
  const response = await authFetch('/notifications/preferences');

  if (!response.ok) {
    throw new Error(`Failed to fetch preferences: ${response.status}`);
  }

  return response.json();
}

/**
 * Update notification channel preferences (partial update).
 */
export async function updatePreferences(
  prefs: Partial<NotificationPreferences>
): Promise<NotificationPreferences> {
  const response = await authFetch('/notifications/preferences', {
    method: 'PATCH',
    body: JSON.stringify(prefs),
  });

  if (!response.ok) {
    throw new Error(`Failed to update preferences: ${response.status}`);
  }

  return response.json();
}
